import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { staffApi, formatApiError } from '@/lib/api';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Search, PlusCircle } from 'lucide-react';
import { toast } from 'sonner';

const STATUS_STYLES = {
  ACTIVE: 'bg-coriander/10 text-coriander',
  PENDING: 'bg-amber-100 text-amber-700',
  PAUSED: 'bg-slate-100 text-slate-600',
  EXPIRED: 'bg-red-100 text-red-700',
  CANCELLED: 'bg-slate-100 text-slate-500',
};

function NewSubscriptionDialog({ onDone }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [customer, setCustomer] = useState(null);
  const [plans, setPlans] = useState([]);
  const [planId, setPlanId] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) staffApi.get('/plans', { params: { active: true } }).then(({ data }) => setPlans(data.plans));
  }, [open]);

  useEffect(() => {
    if (!q.trim()) return setResults([]);
    const t = setTimeout(() => staffApi.get('/customers', { params: { q, pageSize: 8 } }).then(({ data }) => setResults(data.customers)), 250);
    return () => clearTimeout(t);
  }, [q]);

  const submit = async () => {
    setBusy(true);
    try {
      await staffApi.post('/subscriptions', { customerId: customer.id, planId });
      toast.success('Subscription created');
      setOpen(false);
      setCustomer(null);
      setQ('');
      setPlanId('');
      onDone();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="subscription-create-btn" className="bg-saffron hover:bg-saffron-hover">
          <PlusCircle size={16} className="mr-2" /> New Subscription
        </Button>
      </DialogTrigger>
      <DialogContent data-testid="subscription-create-dialog">
        <DialogHeader><DialogTitle>New Subscription</DialogTitle></DialogHeader>

        {!customer && (
          <div className="space-y-2">
            <Input data-testid="subscription-customer-search" placeholder="Search customer…" value={q} onChange={(e) => setQ(e.target.value)} />
            <div className="max-h-48 overflow-y-auto space-y-1">
              {results.map((c) => (
                <button key={c.id} data-testid="subscription-customer-option" onClick={() => setCustomer(c)} className="w-full text-left px-3 py-2 rounded-lg hover:bg-bone text-sm">
                  {c.name} · {c.phone}
                </button>
              ))}
            </div>
          </div>
        )}

        {customer && (
          <div className="space-y-3">
            <p className="text-sm font-medium">{customer.name}</p>
            <div className="space-y-1.5">
              <Label>Plan</Label>
              <Select value={planId} onValueChange={setPlanId}>
                <SelectTrigger data-testid="subscription-plan-select"><SelectValue placeholder="Select a plan" /></SelectTrigger>
                <SelectContent>
                  {plans.map((p) => (
                    <SelectItem key={p.id} value={p.id}>{p.name} — {p.tiffinCount} tiffins — ${Number(p.price).toFixed(2)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button data-testid="subscription-create-confirm-btn" disabled={!customer || !planId || busy} onClick={submit} className="bg-saffron hover:bg-saffron-hover">
            {busy ? 'Creating…' : 'Create Subscription'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const SMART_FILTERS = [
  { key: 'active', label: 'Active', counterKey: 'active' },
  { key: 'expiringSoon', label: 'Expiring Soon', counterKey: 'expiringSoon' },
  { key: 'lowBalance', label: 'Low Balance', counterKey: 'lowBalance' },
  { key: 'paused', label: 'Paused', counterKey: 'paused' },
  { key: 'recentTopup', label: 'Recently Topped Up', counterKey: 'recentTopup' },
  { key: 'pending', label: 'Pending', counterKey: 'pending' },
  { key: 'expired', label: 'Expired', counterKey: 'expired' },
];

export default function Subscriptions() {
  const [searchParams, setSearchParams] = useSearchParams();
  const status = searchParams.get('status') || '';
  const smartParam = searchParams.get('smart') || '';
  const selectedFilters = smartParam.split(',').filter(Boolean);
  const [q, setQ] = useState('');
  const [sortBy, setSortBy] = useState('');
  const [sortDir, setSortDir] = useState('desc');
  const [subscriptions, setSubscriptions] = useState([]);
  const [counters, setCounters] = useState(null);
  const [total, setTotal] = useState(0);

  const loadCounters = () => staffApi.get('/subscriptions/counters').then(({ data }) => setCounters(data));

  const loadSubscriptions = () => {
    const params = { q };
    if (status) params.status = status;
    if (smartParam) params.smart = smartParam;
    if (sortBy) { params.sortBy = sortBy; params.sortDir = sortDir; }
    return staffApi.get('/subscriptions', { params }).then(({ data }) => {
      setSubscriptions(data.subscriptions);
      setTotal(data.total);
    });
  };

  useEffect(() => {
    loadCounters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const t = setTimeout(loadSubscriptions, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, status, smartParam, sortBy, sortDir]);

  const reload = () => {
    loadCounters();
    loadSubscriptions();
  };

  const applySort = (column, dir) => {
    setSortBy(column);
    setSortDir(dir);
  };

  const toggleFilter = (key) => {
    const next = selectedFilters.includes(key) ? selectedFilters.filter((k) => k !== key) : [...selectedFilters, key];
    const params = new URLSearchParams(searchParams);
    if (next.length) params.set('smart', next.join(',')); else params.delete('smart');
    setSearchParams(params);
  };

  const clearSmartFilters = () => {
    const params = new URLSearchParams(searchParams);
    params.delete('smart');
    setSearchParams(params);
  };

  const clearStatusFilter = () => {
    const params = new URLSearchParams(searchParams);
    params.delete('status');
    setSearchParams(params);
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <h1 className="font-heading text-2xl font-700 text-charcoal">Subscriptions</h1>
        <div className="flex items-center gap-2">
          <Link to="/admin/subscriptions/cancelled" data-testid="view-cancelled-btn">
            <Button variant="outline">View Cancelled</Button>
          </Link>
          <NewSubscriptionDialog onDone={reload} />
        </div>
      </div>

      <div className="space-y-2">
        <p className="text-xs font-medium text-muted-ink uppercase tracking-wide">Smart Filters (select any that apply)</p>
        <div className="flex flex-wrap gap-2">
          {SMART_FILTERS.map((f) => {
            const active = selectedFilters.includes(f.key);
            const count = counters?.[f.counterKey];
            const isEmpty = count === 0;
            return (
              <button
                key={f.key}
                type="button"
                data-testid={`smart-filter-${f.key}`}
                onClick={() => toggleFilter(f.key)}
                className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm font-medium border transition-colors duration-150 ${
                  active
                    ? 'bg-saffron text-white border-saffron'
                    : isEmpty
                      ? 'bg-white text-muted-ink/60 border-border/30'
                      : 'bg-white text-charcoal border-border/40 hover:border-saffron/50'
                }`}
              >
                {f.label}
                <span className={`text-xs rounded-full px-1.5 ${active ? 'bg-charcoal/20 text-white' : 'bg-bone text-muted-ink'}`}>
                  {count ?? '—'}
                </span>
              </button>
            );
          })}
          {selectedFilters.length > 0 && (
            <button
              type="button"
              data-testid="smart-filter-clear"
              onClick={clearSmartFilters}
              className="text-xs text-muted-ink underline self-center"
            >
              Clear filters
            </button>
          )}
        </div>
        {status && (
          <div className="flex items-center gap-2 text-sm text-muted-ink">
            <span>
              Filtered by status: <strong className="text-charcoal">{status}</strong>
            </span>
            <button type="button" data-testid="status-filter-clear" onClick={clearStatusFilter} className="text-saffron underline">
              Clear
            </button>
          </div>
        )}
      </div>

      <div className="relative max-w-md">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-ink" />
        <Input
          data-testid="subscriptions-search-input"
          placeholder="Search by customer name, phone, email or ID…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="pl-9"
        />
      </div>

      <div className="bg-white rounded-xl border border-border/40 overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="bg-bone/60">
              <TableHead>Customer</TableHead>
              <TableHead>Phone</TableHead>
              <TableHead>
                <p>Plan</p>
                <Select value={sortBy === 'plan' ? sortDir : ''} onValueChange={(v) => applySort('plan', v)}>
                  <SelectTrigger data-testid="sort-plan-select" className="h-6 text-xs mt-1 w-28 px-2">
                    <SelectValue placeholder="Sort" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="asc">Display Order (Low–High)</SelectItem>
                    <SelectItem value="desc">Display Order (High–Low)</SelectItem>
                  </SelectContent>
                </Select>
              </TableHead>
              <TableHead>Remaining</TableHead>
              <TableHead>
                <p>Expiry</p>
                <Select value={sortBy === 'expiryDate' ? sortDir : ''} onValueChange={(v) => applySort('expiryDate', v)}>
                  <SelectTrigger data-testid="sort-expiry-select" className="h-6 text-xs mt-1 w-32 px-2">
                    <SelectValue placeholder="Sort" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="asc">Soonest First</SelectItem>
                    <SelectItem value="desc">Latest First</SelectItem>
                  </SelectContent>
                </Select>
              </TableHead>
              <TableHead>
                <p>Status</p>
                <Select value={sortBy === 'status' ? sortDir : ''} onValueChange={(v) => applySort('status', v)}>
                  <SelectTrigger data-testid="sort-status-select" className="h-6 text-xs mt-1 w-40 px-2">
                    <SelectValue placeholder="Sort" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="asc">Active → Pending → Paused</SelectItem>
                    <SelectItem value="desc">Paused → Pending → Active</SelectItem>
                  </SelectContent>
                </Select>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {subscriptions.map((s) => (
              <TableRow key={s.id} data-testid="subscription-row" className="hover:bg-bone/50 transition-colors duration-150">
                <TableCell><Link to={`/admin/subscriptions/${s.id}`} className="font-medium text-charcoal block">{s.customer.name}</Link></TableCell>
                <TableCell><Link to={`/admin/subscriptions/${s.id}`} className="block">{s.customer.phone}</Link></TableCell>
                <TableCell><Link to={`/admin/subscriptions/${s.id}`} className="block">{s.plan.name}</Link></TableCell>
                <TableCell><Link to={`/admin/subscriptions/${s.id}`} className="block">{s.remainingCredits}/{s.originalCredits}</Link></TableCell>
                <TableCell><Link to={`/admin/subscriptions/${s.id}`} className="block">{new Date(s.expiryDate).toLocaleDateString('en-CA')}</Link></TableCell>
                <TableCell>
                  <Link to={`/admin/subscriptions/${s.id}`} className={`inline-block text-xs font-semibold px-2 py-0.5 rounded-full ${STATUS_STYLES[s.status] || 'bg-slate-100 text-slate-600'}`}>
                    {s.status}
                  </Link>
                </TableCell>
              </TableRow>
            ))}
            {subscriptions.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} className="text-center text-muted-ink py-8">No subscriptions found.</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      <p className="text-xs text-muted-ink">{total} result(s)</p>
    </div>
  );
}
